import { useEffect, useState, useMemo } from "react";
import {
    Shield, Server, Cpu, Activity, Layers, ChevronLeft, ChevronRight,
    ShieldCheck, HardDrive, Key,
} from "lucide-react";
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from "recharts";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import { showError } from "../../utils/toast";
import { useTheme } from "../../context/ThemeContext";

// --- Interfaces based on the JSON payload ---

interface Inventory {
    totalFirewalls: number;
    uniqueTypes: number;
    uniqueHostnames: number;
    avgCpuUsage: number;
    avgMemoryUsage: number;
}

interface HealthEntry {
    asset_id: number;
    firewall_name: string;
    ip_address: string;
    cpu_memory: string; // JSON string: { memory_used_mb: number, memory_total_mb: number, cpu_usage_percent: number, memory_used_percent: number }
}

interface SecurityPosture {
    asset_id: number;
    firewall_name: string;
    ip_address: string;
    results: string; // JSON string
}

interface VpnTunnels {
    totalTunnels: number;
    tunnelsUp: number;
    tunnelsDown: number;
    totalTrafficInGB: number;
    totalTrafficOutGB: number;
    affectedFirewalls: number;
    averageConnections: number;
    uptimePercentage: number;
}

interface Compliance {
    totalChecks: number;
    compliant: number;
    nonCompliant: number;
    errors: number;
    affectedFirewalls: number;
    compliancePercentage: number;
}

interface DashboardData {
    inventory: Inventory;
    health: HealthEntry[];
    security_posture: SecurityPosture[];
    vpn_tunnels: VpnTunnels;
    compliance: Compliance;
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded-lg shadow-xl z-50">
                <p className="text-gray-900 dark:text-gray-100 text-[10px] font-bold mb-1 uppercase tracking-wider">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-xs mb-0.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry?.color || entry?.payload?.fill }} />
                        <span className="text-gray-600 dark:text-gray-300">{entry?.name}:</span>
                        <span className="text-gray-900 dark:text-white font-mono font-medium">{entry?.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const StatMini = ({ icon: Icon, label, value, color, bg }: any) => (
    <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 min-w-0 shadow-sm">
        <div className={`p-1.5 rounded-lg ${bg} shrink-0`}>
            <Icon className={`w-3.5 h-3.5 ${color}`} />
        </div>
        <div className="min-w-0">
            <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest leading-none mb-0.5 truncate">{label}</p>
            <p className="text-sm font-black text-gray-900 dark:text-white leading-none">{value || 0}</p>
        </div>
    </div>
);

const VpnStatCard = ({ label, value, color = "text-gray-900 dark:text-white", sub }: {
    label: string; value: any; color?: string; sub?: string
}) => (
    <div className="flex flex-col justify-between bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600/50 rounded-lg p-2 min-h-[48px]">
        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none">{label}</span>
        <div className="flex items-baseline gap-1 mt-1">
            <span className={`text-sm font-black leading-none ${color}`}>{value ?? "—"}</span>
            {sub && <span className="text-[8px] text-gray-400 font-medium">{sub}</span>}
        </div>
    </div>
);

const FirewallDashboard = ({ selectedFirewall }: { selectedFirewall: any }) => {
    const currentLevel = useClientLevel();
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DashboardData | null>(null);

    // Pagination
    const [healthPage, setHealthPage] = useState(0);
    const [securityPage, setSecurityPage] = useState(0);
    const HEALTH_ROWS = 10;
    const SEC_ROWS = 10;

    const isGlobal = currentLevel?.name === "global";

    useEffect(() => {
        if (currentLevel) {
            fetchData();
        }
    }, [currentLevel, selectedFirewall]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const client_id = isGlobal ? null : currentLevel?.id;
            const params: any = {};
            if (client_id) params.client_id = client_id;
            if (selectedFirewall?.id) params.firewall_asset_id = selectedFirewall.id;

            const res = await baseapi.get("/dashboard/firewall_unified_dashboard", { params });
            if (res?.data?.status) {
                setData(res.data.data);
            }
        } catch (e: any) {
            showError("Failed to fetch firewall dashboard data");
        } finally {
            setLoading(false);
        }
    };

    // --- Graphs & Derived Data ---

    // 1. Compliance Pie
    const compliancePieData = useMemo(() => {
        if (!data?.compliance) return [];
        return [
            { name: "Compliant", value: data.compliance.compliant || 0, color: "#10b981" },
            { name: "Non-Compliant", value: data.compliance.nonCompliant || 0, color: "#f59e0b" },
            { name: "Errors", value: data.compliance.errors || 0, color: "#f43f5e" },
        ].filter(d => d.value > 0);
    }, [data]);

    const totalChecks = data?.compliance?.totalChecks || 0;

    // 2. Health Bar Chart (CPU + RAM Usage Top 8)
    const resourceUtilData = useMemo(() => {
        if (!data?.health) return [];
        return data.health.map(h => {
            let parsed = { cpu_usage_percent: 0, memory_used_percent: 0 };
            try { parsed = JSON.parse(h.cpu_memory); } catch (e) { }
            return {
                name: h.firewall_name?.length > 12 ? h.firewall_name.substring(0, 12) + "…" : (h.firewall_name || "Unknown"),
                fullName: h.firewall_name || "Unknown",
                cpu: parsed.cpu_usage_percent || 0,
                memory: parsed.memory_used_percent || 0,
                ip: h.ip_address || "-"
            };
        }).sort((a, b) => b.cpu - a.cpu).slice(0, 8); // Sorting primarily by CPU
    }, [data]);

    // Pagination lists
    const filteredHealth = useMemo(() => data?.health || [], [data]);
    const filteredSecurity = useMemo(() => data?.security_posture || [], [data]);

    const healthTotalPages = Math.ceil(filteredHealth.length / HEALTH_ROWS);
    const securityTotalPages = Math.ceil(filteredSecurity.length / SEC_ROWS);

    const getVisiblePages = (current: number, total: number) => {
        if (total <= 3) return Array.from({ length: total }, (_, i) => i);
        let start = Math.max(0, current - 1);
        if (start + 3 > total) start = total - 3;
        return [start, start + 1, start + 2];
    };

    const Pagination = ({ page, setPage, totalPages }: { page: number; setPage: (p: number) => void; totalPages: number }) => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex items-center justify-end gap-1 pt-1.5 border-t border-gray-100 dark:border-gray-700 mt-1">
                <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronLeft className="w-3 h-3" />
                </button>
                {getVisiblePages(page, totalPages).map(pi => (
                    <button key={pi} onClick={() => setPage(pi)}
                        className={`w-5 h-5 text-[9px] font-bold rounded transition-colors ${pi === page ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'}`}>
                        {pi + 1}
                    </button>
                ))}
                <button disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronRight className="w-3 h-3" />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-full flex flex-col items-center justify-center text-gray-700 dark:text-gray-100 py-12">
                <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-3" />
                <p className="text-[11px] font-bold uppercase tracking-widest animate-pulse">Loading Firewall Dashboard...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm py-12">No data available</div>
        );
    }

    const inv = data?.inventory || {
        totalFirewalls: 0, uniqueTypes: 0, uniqueHostnames: 0, avgCpuUsage: 0, avgMemoryUsage: 0
    };
    const vpn = data?.vpn_tunnels || {} as VpnTunnels;

    return (
        <div className="min-h-full xl:h-full bg-slate-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans flex flex-col">
            {/* Inventory Stats Top Row */}
            <div className="flex-none px-2 pt-1 pb-1 z-20">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1.5">
                    <StatMini icon={Shield} label="Total Firewalls" value={inv.totalFirewalls} color="text-orange-500 dark:text-orange-400" bg="bg-orange-500/10" />
                    <StatMini icon={Layers} label="Unique Types" value={inv.uniqueTypes} color="text-blue-500 dark:text-blue-400" bg="bg-blue-500/10" />
                    <StatMini icon={Server} label="Hostnames" value={inv.uniqueHostnames} color="text-violet-500 dark:text-violet-400" bg="bg-violet-500/10" />
                    <StatMini icon={Cpu} label="Avg CPU Usage" value={`${inv.avgCpuUsage?.toFixed(1)}%`} color="text-cyan-600 dark:text-cyan-400" bg="bg-cyan-500/10" />
                    <StatMini icon={HardDrive} label="Avg RAM Usage" value={`${inv.avgMemoryUsage?.toFixed(1)}%`} color="text-emerald-600 dark:text-emerald-400" bg="bg-emerald-500/10" />
                    <StatMini icon={Key} label="VPN Tunnels" value={vpn.totalTunnels || 0} color="text-amber-500 dark:text-amber-400" bg="bg-amber-500/10" />
                    <StatMini icon={Activity} label="VPN Uptime" value={`${vpn.uptimePercentage || 0}%`} color="text-pink-500 dark:text-pink-400" bg="bg-pink-500/10" />
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col px-2 py-1 gap-2">

                {/* TOP ROW: VPN Tunnel Details + Compliance Pie + CPU/RAM Utilization Bar */}
                <div className="flex-none xl:flex-1 min-h-0 flex flex-col xl:flex-row gap-2">

                    {/* VPN Tunnel Details */}
                    <div className="w-full xl:w-[30%] min-h-[300px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm overflow-y-auto custom-scroll">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-2 shrink-0">
                            <div className="p-1 rounded bg-amber-500/10 text-amber-500 dark:text-amber-400"><Key className="w-3 h-3" /></div>
                            VPN Tunnels
                        </h3>

                        {/* Tunnel Up/Down Status Banner */}
                        <div className="flex gap-1.5 mb-2 shrink-0">
                            <div className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg py-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
                                <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                                    {vpn.tunnelsUp ?? 0} Up
                                </span>
                            </div>
                            <div className="flex-1 flex items-center justify-center gap-1.5 bg-rose-500/10 border border-rose-500/20 rounded-lg py-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 dark:bg-rose-400" />
                                <span className="text-[9px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest">
                                    {vpn.tunnelsDown ?? 0} Down
                                </span>
                            </div>
                        </div>

                        {/* All VPN Fields Grid */}
                        <div className="grid grid-cols-2 gap-1.5 shrink-0 pb-1">
                            <VpnStatCard
                                label="Total Tunnels"
                                value={vpn.totalTunnels ?? 0}
                                color="text-amber-500 dark:text-amber-400"
                            />
                            <VpnStatCard
                                label="Uptime"
                                value={`${vpn.uptimePercentage ?? 0}%`}
                                color={
                                    (vpn.uptimePercentage ?? 0) >= 99
                                        ? "text-emerald-600 dark:text-emerald-400"
                                        : (vpn.uptimePercentage ?? 0) >= 80
                                            ? "text-amber-500 dark:text-amber-400"
                                            : "text-rose-500 dark:text-rose-400"
                                }
                            />
                            <VpnStatCard
                                label="Traffic In"
                                value={(vpn.totalTrafficInGB ?? 0).toFixed(2)}
                                sub="GB"
                                color="text-sky-500 dark:text-sky-400"
                            />
                            <VpnStatCard
                                label="Traffic Out"
                                value={(vpn.totalTrafficOutGB ?? 0).toFixed(2)}
                                sub="GB"
                                color="text-orange-500 dark:text-orange-400"
                            />
                            <VpnStatCard
                                label="Avg Connections"
                                value={vpn.averageConnections ?? 0}
                                color="text-violet-500 dark:text-violet-400"
                            />
                            <VpnStatCard
                                label="Affected FWs"
                                value={vpn.affectedFirewalls ?? 0}
                                color="text-pink-500 dark:text-pink-400"
                            />
                        </div>
                    </div>

                    {/* Compliance Pie */}
                    <div className="w-full xl:w-[22%] min-h-[320px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1 shrink-0">
                            <div className="p-1 rounded bg-emerald-500/10 text-emerald-500 dark:text-emerald-400"><ShieldCheck className="w-3 h-3" /></div>
                            Compliance
                        </h3>

                        {/* Compliance % highlight */}
                        <div className="flex items-center justify-center mb-1 shrink-0">
                            <span className={`text-2xl font-black ${(data?.compliance?.compliancePercentage ?? 0) >= 80
                                ? "text-emerald-600 dark:text-emerald-400"
                                : (data?.compliance?.compliancePercentage ?? 0) >= 50
                                    ? "text-amber-500 dark:text-amber-400"
                                    : "text-rose-500 dark:text-rose-400"
                                }`}>
                                {(data?.compliance?.compliancePercentage ?? 0).toFixed(1)}%
                            </span>
                        </div>

                        <div className="h-[200px] xl:h-full xl:flex-1 w-full min-h-[180px]">
                            {compliancePieData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-500">No Data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={compliancePieData} innerRadius="45%" outerRadius="72%" paddingAngle={3} dataKey="value" cornerRadius={4}>
                                            {compliancePieData.map((e, i) => <Cell key={i} fill={e?.color} stroke="none" />)}
                                            <Label
                                                content={({ viewBox: { cx, cy } }: any) => (
                                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                        <tspan x={cx} dy="-0.1em" fill={isDark ? "#f8fafc" : "#0f172a"} fontSize="14" fontWeight="bold">{totalChecks}</tspan>
                                                        <tspan x={cx} dy="1.3em" fill={isDark ? "#9ca3af" : "#64748b"} fontSize="7" fontWeight="600">CHECKS</tspan>
                                                    </text>
                                                )}
                                            />
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Legend */}
                        <div className="flex flex-col gap-0.5 mt-1 shrink-0">
                            {compliancePieData.map((e, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />
                                        <span className="text-[8px] text-gray-600 dark:text-gray-300 font-semibold">{e.name}</span>
                                    </div>
                                    <span className="text-[8px] font-black text-gray-900 dark:text-white font-mono">{e.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CPU & Memory Utilization Bar */}
                    <div className="w-full xl:flex-1 min-h-[360px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1 shrink-0">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-cyan-500/10 text-cyan-500 dark:text-cyan-400"><Cpu className="w-3 h-3" /></div>
                                CPU & Memory Utilization
                            </h3>
                        </div>

                        <div className="h-[280px] xl:h-full xl:flex-1 w-full min-h-[240px]">
                            {resourceUtilData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-500">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={resourceUtilData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }} barCategoryGap="16%">
                                        <defs>
                                            {/* CPU Gradients */}
                                            <linearGradient id="cpuNorm" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="cpuWarn" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="cpuCrit" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.9} />
                                            </linearGradient>
                                            {/* Memory Gradients */}
                                            <linearGradient id="memNorm" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#10b981" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="memWarn" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#eab308" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#eab308" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="memCrit" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.9} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} horizontal={false} />
                                        <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#64748b', fontSize: 8 }} tickCount={5} />
                                        <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={100} interval={0}
                                            tick={({ x, y, payload }: any) => (
                                                <text x={x - 4} y={y} textAnchor="end" dominantBaseline="central" fill={isDark ? "#f5f6f7" : "#334155"} fontSize={8}>
                                                    <title>{payload?.value}</title>
                                                    {payload?.value}
                                                </text>
                                            )}
                                        />
                                        <Tooltip content={({ active, payload }: any) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0]?.payload;
                                            return (
                                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-xl">
                                                    <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold mb-0.5">{d?.fullName || "Unknown"}</p>
                                                    <p className="text-[9px] text-gray-500 dark:text-gray-400 mb-1">IP: {d?.ip || "-"}</p>
                                                    <div className="flex flex-col gap-0.5">
                                                        <div className="flex justify-between gap-4">
                                                            <span className="text-[9px] text-gray-500 dark:text-gray-400">CPU</span>
                                                            <span className="text-[10px] font-mono font-bold text-sky-500 dark:text-sky-400">{d?.cpu || 0}%</span>
                                                        </div>
                                                        <div className="flex justify-between gap-4">
                                                            <span className="text-[9px] text-gray-500 dark:text-gray-400">Memory</span>
                                                            <span className="text-[10px] font-mono font-bold text-emerald-500 dark:text-emerald-400">{d?.memory || 0}%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }} cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }} />
                                        <Legend
                                            verticalAlign="top"
                                            height={16}
                                            iconType="circle"
                                            iconSize={6}
                                            wrapperStyle={{ fontSize: '9px', fontWeight: 'bold' }}
                                            formatter={(value) => <span className="text-gray-700 dark:text-gray-300">{value}</span>}
                                        />

                                        {/* Adding the base "fill" here fixes the legend dot color, while <Cell> keeps the gradients on the bars */}
                                        <Bar dataKey="cpu" name="CPU %" radius={[0, 4, 4, 0]} barSize={8} fill="#0ea5e9">
                                            {resourceUtilData.map((entry, i) => (
                                                <Cell key={`cpu-${i}`} fill={entry.cpu > 90 ? 'url(#cpuCrit)' : entry.cpu > 70 ? 'url(#cpuWarn)' : 'url(#cpuNorm)'} />
                                            ))}
                                        </Bar>

                                        <Bar dataKey="memory" name="Memory %" radius={[0, 4, 4, 0]} barSize={8} fill="#10b981">
                                            {resourceUtilData.map((entry, i) => (
                                                <Cell key={`mem-${i}`} fill={entry.memory > 90 ? 'url(#memCrit)' : entry.memory > 70 ? 'url(#memWarn)' : 'url(#memNorm)'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* BOTTOM ROW: Firewall Details + Security Posture */}
                <div className="flex-none xl:flex-[1.2] min-h-0 flex flex-col xl:flex-row gap-2 pb-4 xl:pb-0">

                    {/* Hardware / Resource Health Table */}
                    <div className="w-full xl:w-1/2 h-[380px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 shrink-0">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-orange-500/10 text-orange-500 dark:text-orange-400"><Server className="w-3 h-3" /></div>
                                Firewall Health ({filteredHealth.length})
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 overflow-x-auto custom-scroll flex flex-col">
                            <div className="min-w-[500px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-3">Name</div>
                                    <div className="col-span-3">IP</div>
                                    <div className="col-span-2 text-center">CPU Usage</div>
                                    <div className="col-span-4 text-center">Memory Usage (MB)</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredHealth.slice(healthPage * HEALTH_ROWS, (healthPage + 1) * HEALTH_ROWS).map((fw, idx) => {
                                        let metrics = { cpu_usage_percent: 0, memory_used_mb: 0, memory_total_mb: 0, memory_used_percent: 0 };
                                        try { metrics = JSON.parse(fw.cpu_memory); } catch (e) { }

                                        return (
                                            <div key={`${fw?.asset_id}-${idx}`}
                                                className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer items-center"
                                            >
                                                <div className="col-span-3 text-[9px] text-gray-900 dark:text-gray-100 font-semibold truncate" title={fw?.firewall_name}>{fw?.firewall_name || "—"}</div>
                                                <div className="col-span-3 text-[9px] text-gray-600 dark:text-gray-300 font-mono truncate">{fw?.ip_address || "—"}</div>
                                                <div className="col-span-2 text-center">
                                                    <span className={`text-[9px] font-bold ${metrics.cpu_usage_percent > 80 ? 'text-rose-500 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                                        {metrics.cpu_usage_percent}%
                                                    </span>
                                                </div>
                                                <div className="col-span-4 text-center">
                                                    <span className="text-[9px] font-bold text-cyan-600 dark:text-cyan-400">{metrics.memory_used_mb?.toFixed(0)}</span>
                                                    <span className="text-[8px] text-gray-400"> / {metrics.memory_total_mb?.toFixed(0)} MB ({metrics.memory_used_percent}%)</span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <Pagination page={healthPage} setPage={setHealthPage} totalPages={healthTotalPages} />
                            </div>
                        </div>
                    </div>

                    {/* Security Posture Table */}
                    <div className="w-full xl:w-1/2 h-[380px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 shrink-0">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-violet-500/10 text-violet-500 dark:text-violet-400"><Shield className="w-3 h-3" /></div>
                                Security Profiles ({filteredSecurity.length})
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 overflow-x-auto custom-scroll flex flex-col">
                            <div className="min-w-[450px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-3">Firewall</div>
                                    <div className="col-span-3">IP</div>
                                    <div className="col-span-6">System Info</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredSecurity.slice(securityPage * SEC_ROWS, (securityPage + 1) * SEC_ROWS).map((sec, idx) => {
                                        let info = { firewall_type: "unknown" };
                                        try { info = JSON.parse(sec.results); } catch (e) { }

                                        return (
                                            <div key={`${sec?.asset_id}-${idx}`}
                                                className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center"
                                            >
                                                <div className="col-span-3 text-[9px] text-gray-900 dark:text-gray-100 font-semibold truncate" title={sec?.firewall_name}>{sec?.firewall_name || "—"}</div>
                                                <div className="col-span-3 text-[9px] text-gray-600 dark:text-gray-300 font-mono truncate">{sec?.ip_address || "—"}</div>
                                                <div className="col-span-6 text-[8px] text-gray-600 dark:text-gray-300 truncate">
                                                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 uppercase font-bold text-violet-600 dark:text-violet-400">
                                                        Type: {info.firewall_type || "-"}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <Pagination page={securityPage} setPage={setSecurityPage} totalPages={securityTotalPages} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FirewallDashboard;